import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import SortableContext from '../../components/DragAndDrop/SortableContext';
import { SortableStrategy } from '../../components/DragAndDrop/dragAndDrop.types';
import GroupedCarousel from '../../components/Site/GroupedCarousel';
import SortableSiteCatalogCard from '../../components/Site/SortableSiteCatalogCard';
import EmptyPlaceholder from '../../components/Table/EmptyPlaceholder';
import useMediaStyles from '../../components/useMediaStyles';
import LangEnum from '../../lang/LangEnum';
import { EMPTY_MEDIAS_PATH } from '../../utils/images.constants';
import { Catalog, EmphasisCatalog } from '../catalogs/catalog.types';
import CatalogCardBase from '../catalogs/pages/CatalogListPage/components/CatalogCard/CatalogCardBase';
import { useTranslation } from '../translation/TranslationContext';

type EmphasisCatalogEditProps = {
  catalogs: Catalog[];
  emphasisCatalogs: EmphasisCatalog[];
  loading: boolean;
  onSave: (emphasisCatalog: EmphasisCatalog[]) => void;
  onCancel: () => void;
  onChangeEmphasisCatalogs: (items: EmphasisCatalog[]) => void;
};

const EmphasisCatalogEdit = ({
  catalogs,
  loading,
  onSave,
  onCancel,
  emphasisCatalogs,
  onChangeEmphasisCatalogs,
}: EmphasisCatalogEditProps) => {
  const { translate } = useTranslation();
  const { classes: mediaClasses } = useMediaStyles({});

  const selectedCatalogsIds: String[] = useMemo(
    () => emphasisCatalogs.map((ec) => ec.catalog.id),
    [emphasisCatalogs],
  );

  const handleRemove = useCallback(
    async (emphasisCatalog: EmphasisCatalog) => {
      const newItems = emphasisCatalogs.filter(
        (item) => item.catalog.id !== emphasisCatalog.catalog.id,
      );
      onChangeEmphasisCatalogs(newItems);
    },
    [emphasisCatalogs, onChangeEmphasisCatalogs],
  );

  const unselectCatalog = useCallback(
    (catalog: Catalog) => {
      const emphasisCatalogSelected = emphasisCatalogs.find(
        (empCat) => empCat.catalog.id === catalog.id,
      );

      if (emphasisCatalogSelected) {
        handleRemove(emphasisCatalogSelected);
      }
    },
    [emphasisCatalogs, handleRemove],
  );

  const selectCatalog = useCallback(
    (catalog: Catalog) => {
      const newEmphasisCatalog: EmphasisCatalog = {
        // creates a unique id for drag and drop
        id: uuidv4(),
        catalog,
        position: emphasisCatalogs.length + 1,
      };
      const newInternalEmphasisCatalogs = [...emphasisCatalogs, newEmphasisCatalog];
      onChangeEmphasisCatalogs(newInternalEmphasisCatalogs);
    },
    [emphasisCatalogs, onChangeEmphasisCatalogs],
  );

  const toggleSelectCatalog = useCallback(
    (catalog: Catalog, isSelected: boolean) => {
      if (isSelected) {
        unselectCatalog(catalog);
      } else {
        selectCatalog(catalog);
      }
    },
    [selectCatalog, unselectCatalog],
  );

  const renderCatalogToSelect = useCallback(
    (items: Catalog[]) => (
      <Grid container spacing={1.5}>
        {items.map((item) => {
          const isSelected = selectedCatalogsIds.includes(item.id);
          return (
            <Grid
              key={item.id}
              size={{
                xs: 6,
                sm: 4,
                md: 3,
                lg: 2,
              }}
            >
              <Box className={mediaClasses.root}>
                <CatalogCardBase catalog={item} />
                <Box className={mediaClasses.mediaControlsTop}>
                  <Checkbox
                    className={isSelected ? undefined : 'ActionButton'}
                    color="primary"
                    checked={isSelected}
                    onChange={() => toggleSelectCatalog(item, isSelected)}
                  />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    ),
    [selectedCatalogsIds, toggleSelectCatalog, mediaClasses],
  );

  return (
    <Grid container>
      <Grid size={12}>
        <Box
          sx={{
            pb: 2,
          }}
        >
          <Typography>{translate(LangEnum.SHOWROOM_FEATURED_CATALOGS)}</Typography>
        </Box>
        <SortableContext
          items={emphasisCatalogs}
          onUpdateItems={onChangeEmphasisCatalogs}
          uniqueIdentifier="id"
          OverlayComponent={({ activeIndex }) => (
            <SortableSiteCatalogCard isDragged item={emphasisCatalogs[activeIndex]} />
          )}
          strategy={SortableStrategy.GRID}
        >
          <Grid
            container
            spacing={1.5}
            sx={{
              alignItems: 'stretch',
            }}
          >
            {emphasisCatalogs.map((catalog: EmphasisCatalog) => (
              <Grid
                key={catalog.id}
                id={catalog.id}
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <SortableSiteCatalogCard item={catalog} isDragged={false} onRemove={handleRemove} />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      {!catalogs || catalogs.length === 0 ? (
        <Grid size={12}>
          <EmptyPlaceholder
            src={EMPTY_MEDIAS_PATH}
            message={translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_PLACEHOLDER)}
          />
        </Grid>
      ) : (
        <Grid size={12}>
          <Box
            sx={{
              py: 2,
            }}
          >
            <Typography>{translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_SELECT_MORE)}</Typography>
          </Box>
          <GroupedCarousel items={catalogs} groupSize={6} renderItemGroup={renderCatalogToSelect} />
        </Grid>
      )}
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Grid>
          <Button onClick={() => onCancel()}>{translate(LangEnum.GLOBAL_CANCEL_LABEL)}</Button>
        </Grid>

        <Grid>
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => onSave(emphasisCatalogs)}
            color="primary"
          >
            {loading ? (
              <Fade in={loading}>
                <CircularProgress size={16} />
              </Fade>
            ) : (
              translate(LangEnum.GLOBAL_APPLY)
            )}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EmphasisCatalogEdit;
